import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { MuiRemixLink } from './links';

interface Props {
  showDesktop?: boolean;
  iconInset: Record<string, string>;
}

export default function SkipPackBuilderButton({ showDesktop = true, iconInset }: Props) {
  const [openDialog, setOpenDialog] = useState(false);

  return (
    <>
      <Button
        variant="text"
        onClick={() => setOpenDialog(true)}
        sx={{
          mt: 2,
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '26px',
          letterSpacing: '0.46px',
          textTransform: 'uppercase',
          textAlign: 'center',
          alignItems: 'center',
          width: 'fit-content',
          margin: '0 auto',
          position: 'absolute',
          inset: iconInset,
        }}
      >
        Skip guided version
      </Button>
      <SkipPackBuilderDialog openDialog={openDialog} setOpenDialog={setOpenDialog} />
    </>
  );
}
export function SkipPackBuilderButtonMobile({ iconInset }: Props) {
  const [openDialog, setOpenDialog] = useState(false);

  return (
    <>
      <Tooltip title="Skip guided version" arrow placement="top-start">
        <Stack
          justifyContent="center"
          alignItems="center"
          onClick={() => setOpenDialog(true)}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            inset: iconInset,
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            boxShadow: 3,
            backgroundColor: 'white',
          }}
        >
          <LogoutIcon sx={{ color: 'primary.main' }} />
          <Typography variant="caption" color="primary.main" fontWeight={500} lineHeight="12px">
            Exit
          </Typography>
        </Stack>
      </Tooltip>
      <SkipPackBuilderDialog openDialog={openDialog} setOpenDialog={setOpenDialog} />
    </>
  );
}

export function SkipPackBuilderDialog({
  openDialog,
  setOpenDialog,
}: {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <Dialog onClose={() => setOpenDialog(false)} open={openDialog} sx={{ '.MuiPaper-root': { borderRadius: 3 } }}>
      <Box
        sx={{
          padding: 3,
          textAlign: 'center',
          width: { xs: '100%', md: '520px' },
          maxWidth: { xs: '327px', md: '520px' },
        }}
      >
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <CloseIcon onClick={() => setOpenDialog(false)} sx={{ color: '#757575', cursor: 'pointer' }} />
        </Stack>
        <ErrorIcon
          sx={{
            color: '#00598B',
            backgroundColor: '#EDF4FB',
            padding: 1,
            borderRadius: '50%',
            fontSize: '40px',
          }}
        />
        <DialogTitle
          sx={{
            fontWeight: 500,
            paddingTop: 2,
            paddingBottom: 2,
            px: { xs: 1, md: 3 },
            color: '#212121',
            fontSize: { xs: '16px', md: '24px' },
            lineHeight: { xs: '28px', md: '32px' },
            maxWidth: { md: '30ch' },
            marginInline: 'auto',
          }}
        >
          Would you like to exit the guided version?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pl: 0, pr: 0, pb: { xs: 3, md: 4 } }}>
          <DialogContentText sx={{ fontSize: { xs: '14px', md: '16px' }, fontWeight: 400 }}>
            Exiting the guided version will take you to the advanced version of the Pack Builder.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: { xs: '8px', md: '16px' },
            p: { xs: 0, md: ' 8px 8px 0 8px' },
          }}
        >
          <Button onClick={() => setOpenDialog(false)} sx={{ fontSize: '13px', width: '100%', py: 1 }}>
            CONTINUE GUIDED
          </Button>
          <Button
            LinkComponent={MuiRemixLink}
            href="/build-pack/create"
            variant="contained"
            sx={{
              boxShadow: 'none',
              fontSize: '13px',
              display: 'block',
              width: '100%',
              py: 1,
            }}
          >
            EXIT GUIDED VERSION
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
